import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Globe, Server, Settings, CheckCircle, Clock, XCircle,
  AlertTriangle, DollarSign, ExternalLink, Plus, Search,
  RefreshCw, Edit, Eye, CreditCard, Pause, Play, X,
  Building2, Phone, Mail, MapPin, Palette, Image, FileText
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status configuration
const STATUS_CONFIG = {
  pending: { label: 'Pendiente', color: 'bg-yellow-500', icon: Clock, textColor: 'text-yellow-400' },
  configuring: { label: 'Configurando', color: 'bg-blue-500', icon: Settings, textColor: 'text-blue-400' },
  active: { label: 'Activo', color: 'bg-emerald-500', icon: CheckCircle, textColor: 'text-emerald-400' },
  suspended: { label: 'Suspendido', color: 'bg-red-500', icon: AlertTriangle, textColor: 'text-red-400' },
  cancelled: { label: 'Cancelado', color: 'bg-gray-500', icon: XCircle, textColor: 'text-gray-400' }
};

// Hosting type labels
const HOSTING_LABELS = {
  subdomain: { label: 'Subdominio', icon: '🌐', color: 'bg-cyan-900/50 text-cyan-400' },
  custom_domain: { label: 'Dominio Propio', icon: '🔗', color: 'bg-purple-900/50 text-purple-400' }
};

export function ClientSitesManager({ adminId, subadminId = null, onCreateSite = null }) {
  const [sites, setSites] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: '', hosting_type: '', search: '' });
  const [selectedSite, setSelectedSite] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const headers = subadminId 
    ? { 'X-SubAdmin-Id': subadminId }
    : { 'X-Admin-Id': adminId };

  const fetchSites = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filter.status) params.append('status', filter.status);
      if (filter.hosting_type) params.append('hosting_type', filter.hosting_type);
      if (filter.search) params.append('search', filter.search);

      const response = await axios.get(`${API_URL}/api/client-sites?${params}`, { headers });
      setSites(response.data.sites || []);
      setStats(response.data.stats || null);
    } catch (error) {
      console.error('Error fetching client sites:', error);
      toast.error('Error al cargar sitios');
    } finally {
      setLoading(false);
    }
  }, [filter, adminId, subadminId]);

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  const handleActivate = async (siteId) => {
    try {
      await axios.post(`${API_URL}/api/client-sites/${siteId}/activate`, null, { headers });
      toast.success('Sitio activado correctamente');
      fetchSites();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al activar sitio');
    }
  };

  const handleSuspend = async (siteId) => {
    if (!window.confirm('¿Seguro que quieres suspender este sitio?')) return;
    try {
      await axios.post(`${API_URL}/api/client-sites/${siteId}/suspend`, null, { headers });
      toast.success('Sitio suspendido');
      fetchSites();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al suspender sitio');
    }
  };

  const handleRecordPayment = async (siteId, amount) => {
    try {
      await axios.post(`${API_URL}/api/client-sites/${siteId}/record-payment?amount=${amount}`, null, { headers });
      toast.success(`Pago de ${amount}€ registrado`);
      fetchSites();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar pago');
    }
  };

  const openEditModal = (site) => {
    setSelectedSite(site);
    setEditForm({
      business_name: site.business_info?.name || '',
      business_phone: site.business_info?.phone || '',
      business_email: site.business_info?.email || '',
      business_address: site.business_info?.address || '',
      business_city: site.business_info?.city || '',
      business_slogan: site.business_info?.slogan || '',
      business_description: site.business_info?.description || '',
      logo_url: site.images?.logo_url || '',
      hero_image_url: site.images?.hero_image_url || '',
      primary_color: site.theme?.primary_color || '#10B981',
      secondary_color: site.theme?.secondary_color || '#1F2937',
      monthly_price: site.maintenance_plan?.monthly_price || 0,
      notes: site.notes || ''
    });
    setShowEditModal(true);
  };

  const saveEdit = async () => {
    if (!selectedSite) return;
    try {
      setSaving(true);
      
      const updateData = {
        business_info: {
          name: editForm.business_name,
          phone: editForm.business_phone,
          email: editForm.business_email,
          address: editForm.business_address,
          city: editForm.business_city,
          slogan: editForm.business_slogan,
          description: editForm.business_description
        },
        images: {
          logo_url: editForm.logo_url,
          hero_image_url: editForm.hero_image_url,
          gallery_urls: selectedSite.images?.gallery_urls || []
        },
        theme: {
          primary_color: editForm.primary_color,
          secondary_color: editForm.secondary_color,
          accent_color: selectedSite.theme?.accent_color || '#F59E0B',
          background_color: selectedSite.theme?.background_color || '#111827',
          text_color: selectedSite.theme?.text_color || '#FFFFFF'
        },
        maintenance_plan: {
          ...selectedSite.maintenance_plan,
          monthly_price: parseFloat(editForm.monthly_price) || 0
        },
        notes: editForm.notes
      };

      await axios.put(`${API_URL}/api/client-sites/${selectedSite.id}`, updateData, { headers });
      toast.success('Sitio actualizado');
      setShowEditModal(false);
      fetchSites();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Server className="w-6 h-6 text-cyan-400" />
            Sitios Desplegados
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Gestiona los sitios web de tus clientes
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchSites}
            className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors"
          >
            <RefreshCw className={`w-5 h-5 text-gray-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {onCreateSite && (
            <Button
              onClick={onCreateSite}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="create-site-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Sitio
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
            <div className="flex items-center gap-2 mb-1">
              <Globe className="w-4 h-4 text-gray-400" />
              <span className="text-xs text-gray-400">Total Sitios</span>
            </div>
            <p className="text-2xl font-bold text-white">{stats.total}</p>
          </div>
          <div className="bg-emerald-900/30 rounded-xl p-4 border border-emerald-700/30">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              <span className="text-xs text-emerald-300">Activos</span>
            </div>
            <p className="text-2xl font-bold text-white">{stats.active}</p>
          </div>
          <div className="bg-cyan-900/30 rounded-xl p-4 border border-cyan-700/30">
            <div className="flex items-center gap-2 mb-1">
              <Server className="w-4 h-4 text-cyan-400" />
              <span className="text-xs text-cyan-300">Subdominios</span>
            </div>
            <p className="text-2xl font-bold text-white">{stats.subdomain_count}</p>
            <p className="text-xs text-gray-500">vs {stats.custom_domain_count} dominios propios</p>
          </div>
          <div className="bg-green-900/30 rounded-xl p-4 border border-green-700/30">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="w-4 h-4 text-green-400" />
              <span className="text-xs text-green-300">Ingresos/Mes</span>
            </div>
            <p className="text-2xl font-bold text-white">{stats.monthly_revenue?.toFixed(0) || 0}€</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            placeholder="Buscar sitios..."
            value={filter.search}
            onChange={(e) => setFilter(f => ({ ...f, search: e.target.value }))}
            className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
        </div>
        <select
          value={filter.status}
          onChange={(e) => setFilter(f => ({ ...f, status: e.target.value }))}
          className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-emerald-500"
        >
          <option value="">Todos los estados</option>
          {Object.entries(STATUS_CONFIG).map(([key, config]) => (
            <option key={key} value={key}>{config.label}</option>
          ))}
        </select>
        <select
          value={filter.hosting_type}
          onChange={(e) => setFilter(f => ({ ...f, hosting_type: e.target.value }))}
          className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-emerald-500"
        >
          <option value="">Tipo de hosting</option>
          <option value="subdomain">Subdominio</option>
          <option value="custom_domain">Dominio Propio</option>
        </select>
      </div>

      {/* Sites List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-emerald-400" />
        </div>
      ) : sites.length === 0 ? (
        <div className="text-center py-12 bg-gray-900/50 rounded-xl border border-gray-800">
          <Server className="w-12 h-12 mx-auto mb-4 text-gray-600" />
          <p className="text-gray-400">No hay sitios desplegados</p>
          <p className="text-sm text-gray-500 mt-1">
            Convierte solicitudes en sitios desde el panel de Webs Demo
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {sites.map((site, index) => {
            const statusConfig = STATUS_CONFIG[site.status] || STATUS_CONFIG.pending;
            const hostingConfig = HOSTING_LABELS[site.hosting_type] || HOSTING_LABELS.subdomain;
            const StatusIcon = statusConfig.icon;

            return (
              <motion.div
                key={site.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-gray-900/50 rounded-xl border border-gray-800 overflow-hidden hover:border-gray-700 transition-colors"
              >
                <div className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    {/* Site Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-white truncate">
                          {site.business_info?.name || 'Sin nombre'}
                        </h3>
                        <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${statusConfig.textColor} bg-gray-800`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusConfig.label}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${hostingConfig.color}`}>
                          {hostingConfig.icon} {hostingConfig.label}
                        </span>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
                        <a 
                          href={site.subdomain ? `${API_URL}/api/site/${site.subdomain}` : site.full_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 hover:text-cyan-400 transition-colors"
                        >
                          <Globe className="w-3 h-3" />
                          {site.subdomain ? `${site.subdomain}.lokalgrow.com` : site.custom_domain}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                        <a
                          href={`${API_URL}/api/preview/${site.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 hover:text-emerald-400 transition-colors"
                        >
                          <Eye className="w-3 h-3" />
                          Preview
                        </a>
                        {site.business_info?.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {site.business_info.phone}
                          </span>
                        )}
                        {site.business_info?.city && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {site.business_info.city}
                          </span>
                        )}
                      </div>

                      {/* Maintenance & Payment Info */}
                      <div className="flex flex-wrap items-center gap-4 mt-3">
                        <span className="flex items-center gap-1 text-sm">
                          <DollarSign className="w-4 h-4 text-green-400" />
                          <span className="text-green-400 font-medium">
                            {site.maintenance_plan?.monthly_price || 0}€/mes
                          </span>
                        </span>
                        {site.next_payment_due && (
                          <span className={`flex items-center gap-1 text-xs ${
                            site.payment_status === 'overdue' ? 'text-red-400' : 'text-gray-500'
                          }`}>
                            <Clock className="w-3 h-3" />
                            Próximo pago: {new Date(site.next_payment_due).toLocaleDateString('es-ES')}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => openEditModal(site)}
                        size="sm"
                        variant="outline"
                        className="text-xs"
                        data-testid={`edit-site-${site.id}`}
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Editar
                      </Button>
                      
                      {site.status === 'pending' && (
                        <Button
                          onClick={() => handleActivate(site.id)}
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700 text-xs"
                        >
                          <Play className="w-3 h-3 mr-1" />
                          Activar
                        </Button>
                      )}
                      
                      {site.status === 'active' && (
                        <Button
                          onClick={() => handleSuspend(site.id)}
                          size="sm"
                          variant="outline"
                          className="text-xs text-red-400 border-red-400/50 hover:bg-red-900/30"
                        >
                          <Pause className="w-3 h-3 mr-1" />
                          Suspender
                        </Button>
                      )}
                      
                      {site.status === 'suspended' && (
                        <Button
                          onClick={() => {
                            const amount = window.prompt('Cantidad del pago (€):');
                            if (amount && !isNaN(parseFloat(amount))) {
                              handleRecordPayment(site.id, parseFloat(amount));
                            }
                          }}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-xs"
                        >
                          <CreditCard className="w-3 h-3 mr-1" />
                          Registrar Pago
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Edit Modal */}
      <AnimatePresence>
        {showEditModal && selectedSite && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50"
            onClick={() => setShowEditModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gray-900 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-700"
            >
              <div className="sticky top-0 bg-gray-900 border-b border-gray-800 p-4 flex items-center justify-between">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Settings className="w-5 h-5 text-cyan-400" />
                  Configurar Sitio
                </h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Business Info Section */}
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Información del Negocio
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Nombre del Negocio *</label>
                      <input
                        type="text"
                        value={editForm.business_name}
                        onChange={(e) => setEditForm(f => ({ ...f, business_name: e.target.value }))}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-cyan-500"
                        data-testid="edit-business-name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Teléfono *</label>
                      <input
                        type="tel"
                        value={editForm.business_phone}
                        onChange={(e) => setEditForm(f => ({ ...f, business_phone: e.target.value }))}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-cyan-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Email *</label>
                      <input
                        type="email"
                        value={editForm.business_email}
                        onChange={(e) => setEditForm(f => ({ ...f, business_email: e.target.value }))}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-cyan-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Ciudad</label>
                      <input
                        type="text"
                        value={editForm.business_city}
                        onChange={(e) => setEditForm(f => ({ ...f, business_city: e.target.value }))}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-cyan-500"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs text-gray-500 mb-1">Dirección</label>
                      <input
                        type="text"
                        value={editForm.business_address}
                        onChange={(e) => setEditForm(f => ({ ...f, business_address: e.target.value }))}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-cyan-500"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs text-gray-500 mb-1">Eslogan</label>
                      <input
                        type="text"
                        value={editForm.business_slogan}
                        onChange={(e) => setEditForm(f => ({ ...f, business_slogan: e.target.value }))}
                        placeholder="Tu especialista en..."
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-cyan-500"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs text-gray-500 mb-1">Descripción</label>
                      <textarea
                        value={editForm.business_description}
                        onChange={(e) => setEditForm(f => ({ ...f, business_description: e.target.value }))}
                        rows={3}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-cyan-500 resize-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Images Section */}
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
                    <Image className="w-4 h-4" />
                    Imágenes (URLs de Google Drive u otro hosting)
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">URL del Logo</label>
                      <input
                        type="url"
                        value={editForm.logo_url}
                        onChange={(e) => setEditForm(f => ({ ...f, logo_url: e.target.value }))}
                        placeholder="https://drive.google.com/..."
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-cyan-500"
                      />
                      {editForm.logo_url && (
                        <img src={editForm.logo_url} alt="Logo preview" className="mt-2 h-12 object-contain rounded" onError={(e) => e.target.style.display = 'none'} />
                      )}
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">URL Imagen Principal (Hero)</label>
                      <input
                        type="url"
                        value={editForm.hero_image_url}
                        onChange={(e) => setEditForm(f => ({ ...f, hero_image_url: e.target.value }))}
                        placeholder="https://drive.google.com/..."
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-cyan-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Theme Section */}
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
                    <Palette className="w-4 h-4" />
                    Colores del Sitio
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Color Principal</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={editForm.primary_color}
                          onChange={(e) => setEditForm(f => ({ ...f, primary_color: e.target.value }))}
                          className="w-10 h-10 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={editForm.primary_color}
                          onChange={(e) => setEditForm(f => ({ ...f, primary_color: e.target.value }))}
                          className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Color Secundario</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={editForm.secondary_color}
                          onChange={(e) => setEditForm(f => ({ ...f, secondary_color: e.target.value }))}
                          className="w-10 h-10 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={editForm.secondary_color}
                          onChange={(e) => setEditForm(f => ({ ...f, secondary_color: e.target.value }))}
                          className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Maintenance Section */}
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Plan de Mantenimiento
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Precio Mensual (€)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={editForm.monthly_price}
                        onChange={(e) => setEditForm(f => ({ ...f, monthly_price: e.target.value }))}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-cyan-500"
                        data-testid="edit-monthly-price"
                      />
                    </div>
                    <div className="flex items-end">
                      <p className="text-xs text-gray-500">
                        {selectedSite?.hosting_type === 'subdomain' 
                          ? '💡 Recomendado: 20-40€/mes para subdominio'
                          : '💡 Recomendado: 60-100€/mes para dominio propio'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Notes Section */}
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Notas Internas
                  </h4>
                  <textarea
                    value={editForm.notes}
                    onChange={(e) => setEditForm(f => ({ ...f, notes: e.target.value }))}
                    rows={2}
                    placeholder="Notas sobre el cliente, configuración especial..."
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-cyan-500 resize-none"
                  />
                </div>
              </div>

              {/* Footer Actions */}
              <div className="sticky bottom-0 bg-gray-900 border-t border-gray-800 p-4 flex items-center justify-end gap-3">
                <Button
                  onClick={() => setShowEditModal(false)}
                  variant="outline"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={saveEdit}
                  disabled={saving}
                  className="bg-cyan-600 hover:bg-cyan-700"
                  data-testid="save-site-btn"
                >
                  {saving ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Guardar Cambios
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default ClientSitesManager;
